package com.advaita.cloud.drive.ui.components

import androidx.compose.runtime.Composable
import com.advaita.cloud.drive.ui.store.FileBrowserStore
import com.advaita.cloud.drive.ui.store.extractBatchPrompt
import com.advaita.cloud.drive.ui.store.extractSelectionTip
import com.advaita.cloud.drive.ui.store.extractableSelection
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * The bulk-selection action bar (spec D7): appears between the toolbar and the table
 * as soon as anything is ticked, and disappears when the selection is cleared.
 *
 * Actions, left to right: Extract (whenever EVERY selected item is extractable, at
 * any count), Compress (always), Download (only when every selection is a file),
 * Delete (always).
 *
 * Extract is here as well as on the row because rig validation found the discovery
 * gap: the row icon reads as a share/upload glyph, and the instinct on selecting a
 * zip is to look at the bar. It covers a whole batch because each archive expands
 * independently — N archives is N ordinary jobs, which the queue drains in turn (one
 * at a time per owner, per `maxConcurrentJobsPerOwner`). Download is hidden for a
 * selection containing a folder — a folder has no single blob to fetch, and
 * "download a folder" is Compress.
 *
 * Renders nothing when the selection is empty, so it is cheap to always compose. The
 * caller gates it on the archive flag.
 *
 * Tim (2026-09-28): it acts on the list on screen ([FileBrowserStore.selectionRows]), so in a
 * shared folder it acts on that folder's own selection, and each action shows where the
 * caller's grants allow it ([FileBrowserStore.selectionBarActions]).
 */
@Composable
fun SelectionBar(store: FileBrowserStore) {
    val count = store.selectionIds.size
    if (count == 0) return
    val submitting = store.compressSubmitting
    val busy = store.bulkBusy
    val actions = store.selectionBarActions
    val extractTargets = if (actions.extract) extractableSelection(store.selectionRows, store.selectionIds) else emptyList()
    val extracting = extractTargets.any { it.id in store.extractingIds }

    Div(attrs = { classes("selection-bar") }) {
        Button(attrs = {
            classes("btn", "icon")
            attr("data-tip", "Clear the selection")
            attr("aria-label", "Clear selection")
            onClick { store.clearViewSelection() }
        }) { Icon(IconName.CLOSE) }
        Span(attrs = { classes("selection-count") }) { Text("$count selected") }

        if (extractTargets.isNotEmpty()) {
            val names = extractTargets.map { it.displayName }
            Button(attrs = {
                classes("btn")
                attr("data-tip", extractSelectionTip(names))
                if (extracting || busy) attr("disabled", "")
                onClick { if (!extracting && !busy) store.extractSelection() }
            }) {
                if (extracting) {
                    Span(attrs = { classes("spinner") }) { Icon(IconName.REFRESH) }
                    Text(" Starting…")
                } else {
                    Icon(IconName.EXTRACT)
                    // "Decompress" when every selected item is a plain .gz — those
                    // become sibling files, not folders. The batch prompt owns the
                    // rule, so the button and the dialog cannot disagree.
                    Text(" ${extractBatchPrompt(names).confirmLabel}")
                }
            }
        }

        // On the roots list of Shared with me Compress shows, off, with its reason.
        val compressTip = actions.compressTip
        if (actions.compress || compressTip != null) Button(attrs = {
            classes("btn")
            attr("data-tip", compressTip ?: "Compress the selected items into one archive in this folder")
            if (submitting || busy || compressTip != null) attr("disabled", "")
            onClick { if (!submitting && !busy && compressTip == null) store.compressSelection() }
        }) {
            if (submitting) {
                Span(attrs = { classes("spinner") }) { Icon(IconName.REFRESH) }
                Text(" Starting…")
            } else {
                Text("Compress")
            }
        }

        if (actions.download) {
            Button(attrs = {
                classes("btn")
                attr("data-tip", "Download the selected file${if (count == 1) "" else "s"}")
                if (busy) attr("disabled", "")
                onClick { if (!busy) store.downloadSelection() }
            }) {
                Icon(IconName.DOWNLOAD)
                Text(" Download")
            }
        }

        if (actions.delete) Button(attrs = {
            classes("btn", "danger")
            attr("data-tip", "Delete the selected item${if (count == 1) "" else "s"}")
            if (busy) attr("disabled", "")
            onClick { if (!busy) store.deleteSelection() }
        }) {
            Icon(IconName.TRASH)
            Text(" Delete")
        }
    }
}
